import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { DropdownComponent, ItemSelectedEvent } from '../../dropdown/dropdown.component';
import { OrdersNewState, OrdersNewStore } from './orders-new.store';
import { BomItem } from '../../../models/bom-item.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { first, Observable } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Route } from '../../../utils/route';
import { select } from '../../../utils/select';
import { formatAmount, formatAmountInput, formatNumber, formatPercent } from '../../../utils/format-amount';
import { formatDayMonthYear } from '../../../utils/date-only';
import { isExactBomItemMatch } from '../../../utils/exact-match';
import { TreeSummaryComponent } from '../../tree-summary/tree-summary.component';
import { OrderItemVm, OrderVm } from './models/order-item.view-model';
import { OrderHeader } from './models/order.dto';
import { ClientPositionPickerComponent, ClientPositionSelected } from '../../client-position-picker/client-position-picker.component';
import { OrderNotesComponent } from './components/order-notes/order-notes.component';
import { ItemPhotoComponent } from '../../item-photo/item-photo.component';

type HeaderField = keyof OrderHeader;

@Component({
  selector: 'orders-new-page',
  templateUrl: './orders-new.page.html',
  styleUrl: './orders-new.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe, RouterLink, DropdownComponent, TreeSummaryComponent, ClientPositionPickerComponent, OrderNotesComponent,
    ItemPhotoComponent
  ]
})
export class OrdersNewPage implements OnInit, OnDestroy {
  readonly state$: Observable<OrdersNewState>;

  // double-click edits, like the root amounts — one header field at a time (client-order precedent)
  readonly editingField = signal<HeaderField | undefined>(undefined);
  readonly fieldInput = viewChild<ElementRef<HTMLInputElement>>('fieldInput');

  // double-click edits, like the BOM tree node — one root amount at a time
  readonly editingAmountItemId = signal<string | undefined>(undefined);
  readonly amountInput = viewChild<ElementRef<HTMLInputElement>>('amountInput');

  // POZYCJE's shadow while the zlecenie loads (0127): three rows at the row's height
  readonly shadowRows = [0, 1, 2];

  // the NOTATKI "+" stands before the zlecenie does (0127); the component it drives exists only once it has loaded
  readonly notes = viewChild(OrderNotesComponent);

  formatAmount = formatAmount;
  formatNumber = formatNumber;
  formatAmountInput = formatAmountInput;
  formatPercent = formatPercent;
  formatDayMonthYear = formatDayMonthYear;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: OrdersNewStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    // the input only exists while editing — focus it as soon as it appears (overseer report-input precedent)
    effect(() => {
      const input = this.fieldInput() ?? this.amountInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Route.parseId(params.get('id'));

      this.store.init(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

      this.initParams();
    });
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  // --- header ---

  startEditing(field: HeaderField): void {
    this.editingField.set(field);
  }

  stopEditing(): void {
    this.editingField.set(undefined);
  }

  saveField(field: HeaderField, value: string, current: OrderVm): void {
    this.stopEditing();

    const header: OrderHeader = {
      name: field === 'name' ? value.trim() : current.name,
      // the date input clears to an empty string; the API takes null
      deadline: field === 'deadline' ? (value || null) : current.deadline
    };

    // an empty name is refused by the API — the field reverts instead of sending it
    if (!header.name) {
      return;
    }

    this.store.patchHeader(header).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- notes ---

  // undefined until the first position creates the zlecenie — the card renders empty and the "+" stays hidden
  notesOrderId(state: OrdersNewState): number | undefined {
    const orderId = state.order?.id;

    return typeof orderId === 'number' ? orderId : undefined;
  }

  // --- positions ---

  // undefined when nothing is selected, or when the id no longer resolves (the BOM page's selectedNode guard)
  selectedItem(state: OrdersNewState): OrderItemVm | undefined {
    return state.selectedItemId
      ? state.order?.items.find(item => item.id === state.selectedItemId)
      : undefined;
  }

  // what the one PODSUMOWANIE folds: the selected position, else the whole zlecenie (0097)
  summaryItems(state: OrdersNewState): OrderItemVm[] {
    const selected = this.selectedItem(state);

    return selected ? [selected] : state.order?.items ?? [];
  }

  // every node under the root, each occurrence counted once — how much tree is here, not how many distinct parts
  partCount(item: OrderItemVm): number {
    return item.children.reduce((count, child) => count + 1 + this.partCount(child), 0);
  }

  toggleSelectedItem(item: OrderItemVm): void {
    this.store.toggleSelectedItem(item.id);
  }

  searchBomItems = (searchTerm: string) => this.store.searchBomItems(searchTerm);

  formatBomItem = (item: BomItem) => item.name;

  isExactBomItemMatch = isExactBomItemMatch;

  onNewItemSelected(event: ItemSelectedEvent<BomItem>): void {
    // the create row (+ Utwórz „…”) on a name the catalog does not know creates the BOM item first (JIT BOM, 0110)
    const add = event.item
      ? this.store.createNewOrderItem(event.item.id, event.item.name, event.item.unit)
      : this.store.createBomItemAndAddItem(event.searchTerm.trim());

    add.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // the order bank (0039) opens under the cursor, the add-node-dropdown way
  openPositionPicker(event: MouseEvent): void {
    this.store.openPositionPicker({ x: event.clientX, y: event.clientY });
  }

  onPositionSelected(selected: ClientPositionSelected): void {
    this.store.createNewOrderItemFromPosition(selected).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onPositionPickerClose(): void {
    this.store.closePositionPicker();
  }

  startEditingAmount(item: OrderItemVm): void {
    // placeholders have no backend id yet — nothing to patch
    if (item.data.orderItemId) {
      this.editingAmountItemId.set(item.id);
    }
  }

  stopEditingAmount(): void {
    this.editingAmountItemId.set(undefined);
  }

  saveAmount(item: OrderItemVm, value: string): void {
    this.stopEditingAmount();

    this.store.updateRootAmount(item.id, value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  deleteItem(item: OrderItemVm): void {
    this.store.deleteOrderItem(item.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private initParams(): void {
    this.state$.pipe(select(state => state.order?.id), takeUntilDestroyed(this.destroyRef)).subscribe(orderId => {
      if (!orderId) {
        return;
      }

      this.router.navigate(['../', orderId], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    });
  }
}
